<template>
  <iForm
    ref="form"
    :inline="inline"
    :label-position="labelPosition"
    :label-width="labelWidth"

    :autocomplete="autocomplete"
    @submit.native.prevent="$submit()"
    @reset.native.prevent="$reset()"
  >
    <slot :data="data"/>

    <FormField v-if="baseErrors.length">
      <p
        v-for="error in baseErrors"
        :key="error"
        class="server-error-text"
        v-text="error"/>
    </FormField>

    <FormField v-if="!disabled && !buttonsHide">
      <iButton
        :disabled="!isDirtyData"
        :loading="submitting"
        type="primary"
        html-type="submit"
      >{{ $t(editMode ? 'forms.submit.edit.short' : 'forms.submit.add.short') }}</iButton>
      <iButton
        :disabled="!isDirtyData || submitting"
        html-type="reset"
        style="margin-left: 8px"
      >{{ $t('forms.reset') }}</iButton>
      <iButton
        :disabled="submitting"
        style="margin-left: 8px"
        @click="$cancel()"
      >{{ $t('forms.cancel') }}</iButton>
    </FormField>
  </iForm>
</template>

<script>
import _ from 'lodash';
import FormField from './FormField.vue';

export default {
  name: 'GdlForm',
  provide() { return { $form: this }; },
  $_veeValidate: { validator: 'new' },
  components: { FormField },
  props: {
    action: { type: Function, default: () => {} },
    initData: { type: Object, default: undefined },

    inline: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    labelPosition: { type: String, validator: $ => ['left', 'right', 'top'].includes($), default: 'right' },
    labelWidth: { type: Number, default: undefined },
    autocomplete: { type: String, validator: $ => ['on', 'off'].includes($), default: 'off' },

    buttonsHide: { type: Boolean, default: false },
  },
  data() {
    return {
      data: {},
      formFields: {},

      submitting: false,
    };
  },
  computed: {
    defaults() {
      const defaults = {};
      Object.values(this.formFields).forEach(({ $props }) => {
        const { prop, default: dflt } = $props;
        defaults[prop] = dflt;
      });
      return defaults;
    },
    defaultsData() {
      const { initData = {}, defaults } = this;
      return _.defaultsDeep(
        _.pick(initData, _.keys(defaults)),
        defaults,
      ) |> _.cloneDeep;
    },
    editMode() { return !!this.initData; },
    isDirtyData() { return !_.isEqual(this.data, this.defaultsData); },
    baseErrors() {
      const keys = Object.keys(this.fields);
      const errors = this.errors.collect(null, null, false);
      // compatibility see issue https://github.com/baianat/vee-validate/issues/1472
      if (errors |> _.isArray) _.keyBy(errors, 'field');
      return errors
        |> ($ => _.omit($, keys))
        |> _.values
        |> _.flatten
        |> ($ => _.filter($, error => error.vmId === this.errors.vmId)) // https://github.com/baianat/vee-validate/issues/1473
        |> ($ => _.map($, 'msg'));
    },
  },
  watch: {
    initData: '$reset',
  },
  created() {
    this.$reset();
  },
  methods: {
    // fields
    $registerField(name, field) {
      this.$set(this.formFields, name, field);
      this.$set(this.data, field.prop, this.defaultsData[field.prop]);
    },
    $unregisterField(name) {
      const field = this.formFields[name];
      this.$delete(this.formFields, name);
      this.$delete(this.data, field.prop);
    },
    // data
    $resetData() {
      this.data = _.cloneDeep(this.defaultsData);
    },
    // validations
    $setErrors(errors) {
      const { $validator, _uid: uId } = this;
      Object.keys(errors).forEach((name) => {
        const field = $validator.fields.find({ name })
          || { id: `${name}_${uId}`, name };
        errors[name].forEach((error) => {
          // eslint-disable-next-line no-underscore-dangle
          const fieldError = $validator._base._createFieldError(field, error);
          $validator.errors.add(fieldError);
        });
      });
    },
    $resetErrors() {
      this.$validator.errors.clear();
    },
    $validate(data) {
      return this.$validator.validateAll(data);
    },
    // form
    $submitting(submitting) {
      this.submitting = submitting;
    },
    async $submit() {
      if (this.disabled || this.submitting) return false;
      const { data, $submitting } = this;
      $submitting(true);
      try {
        if (!(await this.$validate(data))) return false;
        this.$resetErrors(); // clear server errors
        const errors = await this.action({ data });
        if (errors) this.$setErrors(errors);
        return !errors;
      } finally {
        $submitting(false);
      }
    },
    $reset() {
      this.$resetData();
      this.$resetErrors();
    },
    $cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped>
.server-error-text {
  line-height: 1;
  color: #ed3f14;
  /* padding-top: 6px;
  margin-left: 200px;
  margin-bottom: 10px; */
}
</style>
